import * as React from 'react';
import {
	ImgHTMLAttributes,
	FC,
	useEffect,
	useState,
	useMemo,
	useCallback,
	useRef,
	ReactElement,
	CSSProperties,
	isValidElement,
	cloneElement
} from 'react';

import { isImageValid, isAbsoluteUrl } from '../utils';
import { EventCategory, Log } from '../sentry';

import { useConfig } from '../context';

import { getCdnUrl, getContentUrl, isSvgUrl } from './utils';
import { getSvgContent } from '@shared/lib/svgCache';
import { SafeHtml } from '@shared/ui/SafeHtml';

interface Props extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> {
	/**
	 * Относительный путь внутри CDN (iataCode подставится автоматически)
	 * или готовый абсолютный URL(используется как есть).
	 */
	path?: string;
	fallbackUrl?: string;
	isBackground?: boolean;
	/** Использовать content-репозиторий (http://cdn.websky.aero/content/{iataCode}/images/{path}) */
	contentRepo?: boolean;
	/** Иконка, которая будет показана, если не удалось загрузить изображение */
	emptyImage?: ReactElement;
	/** Отправлять ошибки в Log (Sentry), иначе — в console.error */
	notifyOnError?: boolean;
}

const CdnImage: FC<Props> = ({
	path,
	fallbackUrl,
	isBackground,
	contentRepo,
	children,
	alt = '',
	emptyImage,
	notifyOnError = false,
	...imgProps
}) => {
	const [primaryFailed, setPrimaryFailed] = useState(false);
	const [fallbackFailed, setFallbackFailed] = useState(false);
	const primaryFailedRef = useRef(primaryFailed);

	useEffect(() => {
		primaryFailedRef.current = primaryFailed;
	}, [primaryFailed]);

	const {
		global: {
			companyInfo: { iataCode }
		}
	} = useConfig();

	const reportError = useCallback(
		(message: string) => {
			if (notifyOnError) {
				Log({
					category: EventCategory.CDN,
					level: 'warning',
					message
				});
			} else {
				console.error(message);
			}
		},
		[notifyOnError]
	);

	// Авто-фолбэк для contentRepo: при ошибке подставляем ZZ вместо iataCode
	const contentFallbackUrl =
		contentRepo && !fallbackUrl && path && !isAbsoluteUrl(path) && iataCode !== 'ZZ'
			? getContentUrl('ZZ', path)
			: undefined;

	const effectiveFallbackUrl = useMemo(() => {
		if (!contentRepo) {
			return fallbackUrl;
		}

		if (fallbackUrl) {
			if (isAbsoluteUrl(fallbackUrl)) {
				reportError(`CdnImage: contentRepo требует относительный fallbackUrl, получен: ${fallbackUrl}`);

				return contentFallbackUrl;
			}

			return getContentUrl('ZZ', fallbackUrl);
		}

		return contentFallbackUrl;
	}, [contentRepo, fallbackUrl, contentFallbackUrl, reportError]);

	useEffect(() => {
		setPrimaryFailed(false);
		setFallbackFailed(false);
	}, [path, effectiveFallbackUrl]);

	/**
	 * Строим итоговый url для сырого пути:
	 * - если путь уже абсолютный — используем как есть
	 * - иначе строим через CDN или content-репозиторий
	 */
	const buildUrl = useCallback(
		(rawPath?: string) => {
			if (!rawPath) {
				return '';
			}

			if (isAbsoluteUrl(rawPath)) {
				return rawPath;
			}

			return contentRepo ? getContentUrl(iataCode, rawPath) : getCdnUrl(iataCode, rawPath);
		},
		[contentRepo, iataCode]
	);

	const primaryUrl = useMemo(() => {
		if (contentRepo && path && isAbsoluteUrl(path)) {
			reportError(`CdnImage: contentRepo требует относительный путь, получен: ${path}`);

			return '';
		}

		return buildUrl(path);
	}, [buildUrl, contentRepo, path, reportError]);

	const resolvedFallbackUrl = useMemo(() => buildUrl(effectiveFallbackUrl), [buildUrl, effectiveFallbackUrl]);

	// Фолбэк, совпадающий с основным URL (например, iataCode уже равен ZZ), бесполезен —
	// без этой проверки imageUrl не изменился бы после ошибки, и повторный эффект загрузки не запустился бы
	const usableFallbackUrl =
		resolvedFallbackUrl && resolvedFallbackUrl !== primaryUrl ? resolvedFallbackUrl : undefined;

	const imageUrl = primaryFailed && usableFallbackUrl ? usableFallbackUrl : primaryUrl;

	const [svgMarkup, setSvgMarkup] = useState('');

	const isSvg = Boolean(imageUrl && isSvgUrl(imageUrl));

	useEffect(() => {
		setSvgMarkup('');

		if (!isSvg || !imageUrl) {
			return undefined;
		}

		let cancelled = false;

		getSvgContent(imageUrl)
			.then(markup => {
				if (!cancelled) {
					setSvgMarkup(markup);
				}
			})
			.catch(() => {
				if (cancelled) {
					return;
				}

				if (primaryFailedRef.current) {
					setFallbackFailed(true);
				} else {
					setPrimaryFailed(true);
				}

				reportError(`CdnImage: не удалось загрузить SVG: ${imageUrl}`);
			});

		return () => {
			cancelled = true;
		};
	}, [imageUrl, isSvg, reportError]);

	useEffect(() => {
		if (!imageUrl || isSvg) {
			return undefined;
		}

		let cancelled = false;

		isImageValid(imageUrl).then(isValid => {
			if (cancelled || isValid) {
				return;
			}

			if (!primaryFailed) {
				setPrimaryFailed(true);
			} else {
				setFallbackFailed(true);
			}

			reportError(`CdnImage: не удалось загрузить изображение: ${imageUrl}`);
		});

		return () => {
			cancelled = true;
		};
	}, [imageUrl, isSvg, primaryFailed, reportError]);

	const allFailed = (primaryFailed && !usableFallbackUrl) || fallbackFailed;

	if (allFailed || !imageUrl) {
		return emptyImage ?? null;
	}

	if (isSvg) {
		if (isBackground) {
			reportError('CdnImage: isBackground не поддерживается для SVG');

			return emptyImage ?? null;
		}

		if (!svgMarkup) {
			return null;
		}

		return (
			<SafeHtml
				sanitizeConfig={{ ADD_TAGS: ['use'] }}
				html={svgMarkup}
				tag={'span'}
				className={imgProps.className}
				style={imgProps.style}
			/>
		);
	}

	if (isBackground) {
		if (isValidElement(children)) {
			return cloneElement(children, {
				style: { backgroundImage: `url(${imageUrl})`, ...imgProps.style }
			} as CSSProperties);
		}

		return (
			<div className={imgProps.className} style={{ backgroundImage: `url(${imageUrl})`, ...imgProps.style }}>
				{children}
			</div>
		);
	}

	return <img src={imageUrl} alt={alt} {...imgProps} />;
};

export default CdnImage;
